import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import { useDialogEscape } from '../../hooks/use-dialog-focus';
import { ModalPortal } from '../../modal/modal-portal';
import { Presence, overlayMotion, preset } from '../../../motion';
import {
  askResidentIq,
  RESIDENT_IQ_MAX_CHARS,
  ResidentIqAnswer,
  ResidentIqPassAction,
  residentIqAvailability,
} from '../../../api/tenant-iq-client';
import { passAction } from '../../../api/tenant-passes-client';
import { Fab } from '../../iq/iq-dock.styled';
import { AuroraButton, GhostButton } from '../../iq/iq-shared.styled';
import { AnswerText } from '../../iq/iq-answer.styled';
import {
  ABubble,
  Chip,
  Composer,
  Counter,
  FloatPanel,
  Header,
  HeaderActions,
  HeaderTitle,
  IconBtn,
  InputRow,
  QBubble,
  Starters,
  TextArea,
  ThinkingDots,
  Thread,
  VisuallyHidden,
  Welcome,
  WelcomeText,
} from '../../iq/iq-chat.styled';
import { AnswerActions, Item, ItemDetail, ItemList, ItemTitle } from './portal-iq.styled';

/**
 * LOBBERO IQ IN THE RESIDENT PORTAL.
 *
 * The same launcher and the same panel the desk has, answering a different
 * question: not "what happened in this building" but "what about MY home". A
 * resident asks about their parcels, their visitors and who is expected, or
 * says who visits and when — "My cleaner Maria comes every Tuesday 9 to 1" —
 * and is taken to the visitor-pass form already filled in, to check and save.
 *
 * ── IT SENDS ONE STRING ──
 * The request is the question and nothing else (api/tenant-iq-client). The
 * server answers for the session's own home; there is no unit or building on
 * this screen to choose, so there is none to get wrong.
 *
 * ── IT NEVER ACTS ON ITS OWN ──
 * A pass is created by the form's own Save button, pressed by the resident,
 * exactly as quick-add works. This panel only carries their sentence there
 * (router state `iqPass`, read once by the Visitors tab). Pausing, resuming,
 * ending a pass or skipping one visit is offered as a BUTTON under the answer;
 * the press sends it through the pass routes the portal already uses.
 *
 * ── WHO SEES IT ──
 * Shown only when the server says it is available (the organisation has
 * Lobbero IQ and the resident switch is on). Somebody who also holds the staff
 * capability already has the desk's launcher in this corner, mounted app-wide;
 * two buttons on one spot would be worse than one, so theirs stays.
 */

const STARTER_COUNT = 3;
const FAILED = 'Lobbero IQ could not answer that just now. Please try again.';

export const PORTAL_IQ_COPY = {
  welcome:
    'Ask about your own home — parcels at the desk, who visited, who is expected. Or tell me who visits and when, and I will set up a visitor pass for you to check.',
  placeholder: 'Ask about your parcels, visitors or passes…',
  reviewPass: 'Review and save the pass',
  working: 'Working…',
} as const;

/* The launcher as a motion element, so it can fade against the panel. */
const MotionFab = motion.create(Fab);

interface Turn {
  q: string;
  res: ResidentIqAnswer | null;
  error?: string;
  /** The answer's button, once pressed: sending, done (its words) or failed. */
  act?: { state: 'busy' | 'done' | 'failed'; text?: string };
}

/**
 * Told to the Visitors tab when a pass was changed from this panel, so its
 * list re-reads in place instead of showing the pass as it was.
 */
export const PASSES_CHANGED_EVENT = 'lobbero:passes-changed';

const messageOf = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message) return message;
  }

  return FAILED;
};

export const PortalIq: React.FC = () => {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const isStaffIq = can(permissions, PERMISSIONS.IQ_USE);
  const [available, setAvailable] = useState(false);
  const [examples, setExamples] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const alive = useRef(true);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    alive.current = true;
    if (isStaffIq) return undefined;
    // Wrapped, so a loader that returns nothing (a mocked client) is "no".
    Promise.resolve()
      .then(() => residentIqAvailability())
      .then(result => {
        if (!alive.current || !result) return;
        setAvailable(result.available === true);
        setExamples(Array.isArray(result.examples) ? result.examples : []);
      })
      .catch(() => undefined);

    return () => {
      alive.current = false;
    };
  }, [isStaffIq]);

  const close = useCallback(() => setIsOpen(false), []);
  // Sits OVER a page the reader may still want to read: on the dialog stack
  // for Escape ordering only, like the desk's panel.
  useDialogEscape(isOpen, close, { locksScroll: false });

  // Focus goes in when it opens and back to the launcher when it closes.
  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      const raf = window.requestAnimationFrame(() => {
        panelRef.current?.focus();
      });

      return () => window.cancelAnimationFrame(raf);
    }
    const previous = returnFocusRef.current;
    returnFocusRef.current = null;
    if (previous && document.contains(previous)) previous.focus();

    return undefined;
  }, [isOpen]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [turns, asking]);

  const send = async (text: string): Promise<void> => {
    const question = text.trim();
    if (!question || question.length > RESIDENT_IQ_MAX_CHARS || asking) return;
    setQ('');
    setAsking(true);
    setTurns(t => [...t, { q: question, res: null }]);
    const settle = (patch: Partial<Turn>) =>
      setTurns(t =>
        t.map((turn, i) => (i === t.length - 1 ? { ...turn, ...patch } : turn))
      );
    try {
      const res = await askResidentIq(question);
      if (!alive.current) return;
      settle({ res });
      // Switched off while the panel was open: say so once, then no launcher.
      if (!res.available) setAvailable(false);
    } catch (error) {
      if (!alive.current) return;
      settle({ error: messageOf(error) });
    } finally {
      if (alive.current) setAsking(false);
    }
  };

  /* THE RESIDENT'S OWN PRESS makes the change — through passAction, the same
     call the pass's own page makes, with its rules, its audit row and its
     notice to the desk. Lobbero IQ only worked out which button to offer. */
  const confirm = async (index: number, action: ResidentIqPassAction): Promise<void> => {
    const mark = (act: Turn['act']) =>
      setTurns(t => t.map((turn, i) => (i === index ? { ...turn, act } : turn)));
    mark({ state: 'busy' });
    try {
      await passAction(action.pass_id, action.action, action.date || undefined);
      if (!alive.current) return;
      mark({ state: 'done', text: action.done });
      window.dispatchEvent(new Event(PASSES_CHANGED_EVENT));
    } catch (error) {
      if (!alive.current) return;
      mark({ state: 'failed', text: messageOf(error) });
    }
  };

  const go = (to: string, state?: unknown): void => {
    setIsOpen(false);
    navigate(to, state === undefined ? undefined : { state });
  };

  if (isStaffIq) return null;
  // Kept while open, so an answer that says "not switched on" can be read.
  if (!available && !isOpen) return null;

  const over = q.length > RESIDENT_IQ_MAX_CHARS;

  return (
    <>
      {/* TWO Presences, not one. Sharing one (as the launcher and panel
          cross-fade) means the panel is not re-rendered while the launcher
          is still leaving, and whatever is typed in that moment is dropped
          — measured: the composer read 0/400 until the exit finished. Each
          in its own, they still fade against each other and the panel is
          live from its first frame. */}
      <Presence>
        {!isOpen ? (
          <MotionFab
            key="portal-iq-fab"
            variants={preset.state}
            initial="hidden"
            animate="visible"
            exit="hidden"
            type="button"
            data-testid="portal-iq-launcher"
            aria-label="Open Lobbero IQ"
            title="Lobbero IQ"
            onClick={() => setIsOpen(true)}
          >
            ✦
          </MotionFab>
        ) : null}
      </Presence>
      <Presence>
        {isOpen ? (
          <ModalPortal key="portal-iq-panel">
            <FloatPanel
              {...overlayMotion('panel')}
              ref={panelRef}
              role="dialog"
              aria-label="Lobbero IQ"
              data-testid="portal-iq-panel"
              tabIndex={-1}
            >
              <Header>
                <HeaderTitle>
                  <span aria-hidden>✦</span>
                  Lobbero IQ
                </HeaderTitle>
                <HeaderActions>
                  <IconBtn
                    type="button"
                    aria-label="Close Lobbero IQ"
                    title="Close"
                    onClick={close}
                  >
                    ✕
                  </IconBtn>
                </HeaderActions>
              </Header>

              {/* Spoken, not only drawn: an answer arrives later, as text. */}
              <Thread aria-live="polite" aria-busy={asking || undefined}>
                <Welcome>
                  <WelcomeText>{PORTAL_IQ_COPY.welcome}</WelcomeText>
                </Welcome>

                {turns.map((turn, i) => (
                  <React.Fragment key={i}>
                    <QBubble>{turn.q}</QBubble>
                    {turn.res ? (
                      <ABubble
                        $wide={turn.res.items.length > 0}
                        data-testid="portal-iq-answer"
                      >
                        <AnswerText>{turn.res.answer}</AnswerText>
                        {turn.act?.text ? (
                          <AnswerText data-testid="portal-iq-outcome" role="status">
                            {turn.act.text}
                          </AnswerText>
                        ) : null}
                        {turn.res.items.length ? (
                          <ItemList>
                            {turn.res.items.map((item, n) => (
                              <Item key={n}>
                                <ItemTitle>{item.title}</ItemTitle>
                                {item.detail ? (
                                  <ItemDetail>{item.detail}</ItemDetail>
                                ) : null}
                              </Item>
                            ))}
                          </ItemList>
                        ) : null}
                        {turn.res.action || turn.res.link ? (
                          <AnswerActions>
                            {turn.res.action?.kind === 'add_pass' ? (
                              <AuroraButton
                                type="button"
                                data-testid="portal-iq-review-pass"
                                onClick={() =>
                                  go('/portal/visitors', {
                                    iqPass:
                                      turn.res?.action?.kind === 'add_pass'
                                        ? turn.res.action.sentence
                                        : undefined,
                                  })
                                }
                              >
                                {PORTAL_IQ_COPY.reviewPass}
                              </AuroraButton>
                            ) : null}
                            {turn.res.action?.kind === 'pass_action' &&
                            turn.act?.state !== 'done' ? (
                              <AuroraButton
                                type="button"
                                data-testid="portal-iq-confirm"
                                disabled={turn.act?.state === 'busy'}
                                onClick={() => {
                                  const action = turn.res?.action;
                                  if (action?.kind === 'pass_action')
                                    void confirm(i, action);
                                }}
                              >
                                {turn.act?.state === 'busy'
                                  ? PORTAL_IQ_COPY.working
                                  : turn.res.action.label}
                              </AuroraButton>
                            ) : null}
                            {turn.res.link ? (
                              <GhostButton
                                type="button"
                                onClick={() => go(turn.res?.link?.to || '/portal')}
                              >
                                {turn.res.link.label}
                              </GhostButton>
                            ) : null}
                          </AnswerActions>
                        ) : null}
                      </ABubble>
                    ) : turn.error ? (
                      <ABubble>{turn.error}</ABubble>
                    ) : (
                      <ABubble>
                        <ThinkingDots aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </ThinkingDots>
                        <VisuallyHidden>Checking your home…</VisuallyHidden>
                      </ABubble>
                    )}
                  </React.Fragment>
                ))}
                <div ref={threadEndRef} />
              </Thread>

              <Composer>
                <Starters>
                  {examples.slice(0, STARTER_COUNT).map(example => (
                    <Chip
                      key={example}
                      type="button"
                      disabled={asking}
                      onClick={() => send(example)}
                    >
                      {example}
                    </Chip>
                  ))}
                </Starters>
                <InputRow>
                  <TextArea
                    value={q}
                    aria-label="Your question"
                    placeholder={PORTAL_IQ_COPY.placeholder}
                    onChange={e => setQ(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void send(q);
                      }
                    }}
                  />
                  <AuroraButton
                    type="button"
                    disabled={asking || !q.trim() || over}
                    onClick={() => send(q)}
                  >
                    Ask
                  </AuroraButton>
                </InputRow>
                <Counter $over={over}>
                  {q.length}/{RESIDENT_IQ_MAX_CHARS}
                  {over ? ' · too long' : ''}
                </Counter>
              </Composer>
            </FloatPanel>
          </ModalPortal>
        ) : null}
      </Presence>
    </>
  );
};
